import type { NavigationNode } from "../api/types";

/** Helpers over a collection's navigation tree (`docs/spec/collections.md` §2.7). */

export interface FlatNode {
  node: NavigationNode;
  depth: number;
  parentId: string | null;
}

export function flattenNodes(
  nodes: readonly NavigationNode[],
  depth = 0,
  parentId: string | null = null,
): FlatNode[] {
  const result: FlatNode[] = [];
  for (const node of nodes) {
    result.push({ node, depth, parentId });
    result.push(...flattenNodes(node.children, depth + 1, node.id));
  }
  return result;
}

export function findNode(nodes: readonly NavigationNode[], id: string): NavigationNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const nested = findNode(node.children, id);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

/** The node and everything under it — the set a move may not target. */
export function subtreeIds(nodes: readonly NavigationNode[], id: string): Set<string> {
  const found = findNode(nodes, id);
  const ids = new Set<string>();
  if (found === undefined) return ids;
  const walk = (node: NavigationNode): void => {
    ids.add(node.id);
    for (const child of node.children) walk(child);
  };
  walk(found);
  return ids;
}

/** The ancestor chain of `id`, outermost first, excluding the node itself. */
export function ancestorsOf(nodes: readonly NavigationNode[], id: string): NavigationNode[] {
  const path: NavigationNode[] = [];
  const walk = (list: readonly NavigationNode[], trail: NavigationNode[]): boolean => {
    for (const node of list) {
      if (node.id === id) {
        path.push(...trail);
        return true;
      }
      if (walk(node.children, [...trail, node])) return true;
    }
    return false;
  };
  walk(nodes, []);
  return path;
}

/** Siblings of `parentId` (the roots when it is null), in order. */
export function siblingsOf(
  nodes: readonly NavigationNode[],
  parentId: string | null,
): NavigationNode[] {
  if (parentId === null) return [...nodes];
  return findNode(nodes, parentId)?.children ?? [];
}

/** Removes a node from a tree, returning a new tree and the node that left. */
export function removeNode(
  nodes: readonly NavigationNode[],
  id: string,
): { nodes: NavigationNode[]; removed: NavigationNode | null } {
  let removed: NavigationNode | null = null;
  const walk = (list: readonly NavigationNode[]): NavigationNode[] => {
    const result: NavigationNode[] = [];
    for (const node of list) {
      if (node.id === id) {
        removed = node;
        continue;
      }
      result.push({ ...node, children: walk(node.children) });
    }
    return result;
  };
  return { nodes: walk(nodes), removed };
}

/** Inserts a node at `position` among `parentId`'s children (the roots when null). */
export function insertNode(
  nodes: readonly NavigationNode[],
  node: NavigationNode,
  parentId: string | null,
  position: number,
): NavigationNode[] {
  if (parentId === null) {
    const next = [...nodes];
    next.splice(Math.max(0, Math.min(position, next.length)), 0, node);
    return next;
  }
  return nodes.map((entry) => {
    if (entry.id === parentId) {
      const children = [...entry.children];
      children.splice(Math.max(0, Math.min(position, children.length)), 0, node);
      return { ...entry, children };
    }
    return { ...entry, children: insertNode(entry.children, node, parentId, position) };
  });
}
